<script>
export default {
  name: "DidoMillesime",
};
</script>

<script setup>
import { computed, ref, watch } from "vue";
import { date } from "quasar";
import api from "../api";
import { displayDateFormatIso, pause } from "../utils";
import DidoMillesimeTabTable from "./millesime/tabs/Table.vue";
import DidoMillesimeTabDownload from "./millesime/tabs/Download.vue";
import DidoMillesimeTabDocumentations from "./millesime/tabs/Documentations.vue";
import DidoMillesimeTabRefsNomencs from "./millesime/tabs/RefsNomencs.vue";
import DidoMillesimeTabHistoricalDatas from "./millesime/tabs/HistoricalDatas.vue";
import DidoMillesimeTabApi from "./millesime/tabs/Api.vue";

// define Props
const props = defineProps({
  apiUrl: { type: String, required: true },
  datafileRid: { type: String, required: true },
  datafileMillesime: { type: String, required: false, default: "" },
  millesimeObject: { type: Object, required: false, default: null },
  selectedTab: { type: String, required: false, default: "datas" },
  initialFilter: { type: String, required: false, default: null },
});

// define events emitted
const emit = defineEmits([
  "millesime-retrieved",
  "millesime-not-retrieved",
  "bad-configuration",
  "tab-changed-in-millesime",
]);

// define Reactive Data
const status = ref("");
const loading = ref(false);
const infos = ref(null);
const initialFilterMillesime = ref(null);
const selectedTab = ref(props.selectedTab);

// function when tab changed
const onTabChanged = (newTab) => emit("tab-changed-in-millesime", newTab);

// define computed data
const configured = computed(() => {
  if (!props.apiUrl) return false;
  if (!props.datafileRid) return false;
  if (!props.datafileMillesime && !props.millesimeObject) return false;
  return true;
});

// define computed data for documentations
const documentations = computed(() => {
  if (infos.value.dataset.attachments)
    return infos.value.dataset.attachments.filter(
      ({ type }) => !type || type === "documentation",
    );
  return [];
});
// define computed data for refs and nomencs
const refsnomencs = computed(() => infos.value.refs);
// define computed data for historical datas
const historicalDatas = computed(() => {
  if (infos.value.dataset.attachments)
    return infos.value.dataset.attachments.filter(
      ({ type }) => type === "historical_data",
    );
  return [];
});

// define watchers
watch(
  () => [
    props.apiUrl,
    props.datafileRid,
    props.datafileMillesime,
    props.millesimeObject,
  ],
  () => {
    status.value = "";
    loading.value = false;
    infos.value = null;
    getMillesimeInfos();
  },
);
watch(
  () => [props.selectedTab],
  () => {
    selectedTab.value = props.selectedTab;
  },
);

// define methods
const getMillesimeInfos = async () => {
  // if widget bad configured
  if (!configured.value) {
    status.value = "bad-configured";
    loading.value = false;
    infos.value = null;
    emit("bad-configuration");
    return;
  }

  // if millesime object data is available , setting it in reactive datas (no api call required !!)
  if (props.millesimeObject) {
    const isPublied =
      date.getDateDiff(
        props.millesimeObject.date_diffusion,
        new Date(),
        "seconds",
      ) < 0;
    infos.value = props.millesimeObject;
    status.value = isPublied ? "available" : "delayed";
  }
  // if datafile rid and millesime are set, api call for retrieving millesime infos and setting it in reactive datas
  if (props.datafileRid && props.datafileMillesime) {
    loading.value = true;
    await Promise.all([
      api.getMillesimeInfos(
        props.apiUrl,
        props.datafileRid,
        props.datafileMillesime,
      ),
      pause(500),
    ])
      .then(([apiResult]) => {
        infos.value = apiResult.infos;
        status.value = apiResult.status;
        emit(
          ["available", "delayed"].includes(status.value)
            ? "millesime-retrieved"
            : "millesime-not-retrieved",
          {
            datafileRid: props.datafileRid,
            datafileMillesime: props.datafileMillesime,
          },
        );
      })
      .finally(() => (loading.value = false));
  }
};

// init
getMillesimeInfos();
initialFilterMillesime.value = props.initialFilter
  ? JSON.parse(props.initialFilter)
  : null;
</script>

<template>
  <div class="dido-widget-millesime relative-position">
    <!-- Bad configuration of widget -->
    <div
      v-if="status === 'bad-configured'"
      class="q-pa-sm row-start-center border-secondary text-black"
    >
      <div>Impossible de charger le composant...</div>
      <div>Les attributs suivant sont requis:</div>
      <ul>
        <li>api-url</li>
        <li>datafile-rid</li>
        <li>datafile-millesime ou millesime-object</li>
      </ul>
    </div>
    <!-- Error with api -->
    <div
      v-if="status == 'error' && !loading"
      class="dido-box-msg q-ma-sm q-pa-sm text-subtitle2 dido-error-msg"
    >
      <QIcon name="mdi-alert" size="sm" class="q-mr-sm" />Erreur durant la
      récupération du millésime (vérifiez l'url d'accès à l'api DiDo)
    </div>

    <!-- Millesime doesn't exist -->
    <div
      v-if="status == 'notFound' && !loading"
      class="dido-box-msg q-ma-sm q-pa-sm text-subtitle2 dido-warning-msg"
    >
      <QIcon name="mdi-information" size="sm" class="q-mr-sm" />Les données
      n'ont pas été retrouvées (vérifiez le rid et le millésime)
    </div>

    <!-- Publication of Millesime is delayed -->
    <div
      v-if="status == 'delayed' && !loading"
      class="dido-box-msg q-ma-sm q-pa-sm text-subtitle2 dido-info-msg"
    >
      <QIcon name="mdi-information" size="sm" class="q-mr-sm" />Les données
      concernant ce millésime seront diffusées le
      {{ displayDateFormatIso(infos.date_diffusion) }}
    </div>

    <!-- Millesime is here -->
    <div v-if="status == 'available' && !loading" class="row border-box">
      <div class="col-12">
        <QTabs
          v-model="selectedTab"
          no-caps
          align="left"
          narrow-indicator
          dense
          active-bg-color="white"
          active-color="secondary"
          indicator-color="secondary"
          class="millesime-tabs"
          @update:model-value="onTabChanged"
        >
          <QTab name="datas" label="Données" />
          <QTab
            v-if="documentations.length"
            name="documentations"
            label="Documentations"
          />
          <QTab
            v-if="refsnomencs.length"
            name="refsnomencs"
            label="Référentiels et nomenclatures"
          />
          <QTab
            v-if="historicalDatas.length"
            name="historicaldatas"
            label="Données historiques"
          />
          <QTab name="download" label="Téléchargement" />
          <QTab name="api" label="Api" />
        </QTabs>
      </div>
      <div class="col-12 q-pa-md">
        <!-- Table of Millesime Datas -->
        <div v-if="selectedTab === 'datas'">
          <DidoMillesimeTabTable
            :api-url="props.apiUrl"
            :rid="infos.rid"
            :millesime="infos.millesime"
            :infos="infos"
            :initial-filter="initialFilterMillesime"
          />
        </div>
        <!-- Documnetations linked to Dataset of Millesime -->
        <div v-if="selectedTab === 'documentations'">
          <DidoMillesimeTabDocumentations
            :api-url="props.apiUrl"
            :documentations="documentations"
          />
        </div>
        <!-- Refs or Nomencs linked to Dataset of Millesime -->
        <div v-if="selectedTab === 'refsnomencs'">
          <DidoMillesimeTabRefsNomencs
            :api-url="props.apiUrl"
            :refsnomencs="refsnomencs"
          />
        </div>
        <!-- Historical datas linked to Dataset of Millesime -->
        <div v-if="selectedTab === 'historicaldatas'">
          <DidoMillesimeTabHistoricalDatas
            :api-url="props.apiUrl"
            :historical-datas="historicalDatas"
          />
        </div>
        <!-- Download of Millesime Data -->
        <div v-if="selectedTab === 'download'">
          <DidoMillesimeTabDownload
            :api-url="props.apiUrl"
            :rid="infos.rid"
            :millesime="infos.millesime"
            :infos="infos"
          />
        </div>
        <!-- Api Documentation of Millesime -->
        <div v-if="selectedTab === 'api'">
          <DidoMillesimeTabApi
            :api-url="props.apiUrl"
            :rid="infos.rid"
            :millesime="infos.millesime"
            :columns="infos.columns"
          />
        </div>
      </div>
    </div>

    <!-- spinner during interaction with api -->
    <QInnerLoading :showing="loading">
      <QSpinner size="50px" color="secondary" />
      <div class="text-h6 text-secondary">Chargement du millesime en cours</div>
    </QInnerLoading>
  </div>
</template>

<style lang="sass" scoped>
.dido-widget-millesime
  min-height: 200px

  :deep( .millesime-tabs .q-tab)
    justify-content: start
</style>
